import { Box } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { routes } from "../config";
import { useLocalization } from "../contexts/LocalizationContext";

interface BottomTabBarProps {
  onQrClick?: () => void;
}

export default function BottomTabBar({ onQrClick }: BottomTabBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLocalization();

  const isWallet = location.pathname === routes.wallet;
  const isRedeem = location.pathname === routes.redeem;
  const isAccount = location.pathname === routes.account;
  const isHome = !isWallet && !isRedeem && !isAccount;

  const activeColor = "#7BA88A";
  const inactiveColor = "#5A5A5A";

  const tabStyle = (active: boolean) => ({
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "3px",
    pb: "4px",
    pt: "6px",
    px: "4px",
    border: "none",
    background: "none",
    cursor: "pointer",
    color: active ? activeColor : inactiveColor,
    fontFamily: "inherit",
    minWidth: 0,
    "&:active": { opacity: 0.7 },
  });

  return (
    <Box
      component="nav"
      aria-label="Main"
      sx={{
        height: "64px",
        px: "6px",
        pb: "10px",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-around",
        backgroundColor: "#fff",
        borderTop: "1px solid #EAEAEA",
        boxShadow: "0 -4px 16px rgba(0,0,0,0.04)",
        maxWidth: "100%",
      }}
    >
      {/* Home */}
      <Box component="button" onClick={() => navigate(routes.home)} sx={tabStyle(isHome)}>
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
          <path d="M12 3.2 3.5 10.2V20a1 1 0 0 0 1 1h5.2v-5.5h4.6V21H19.5a1 1 0 0 0 1-1v-9.8L12 3.2z"/>
        </svg>
        <Box component="span" sx={{ fontSize: "10px", fontWeight: 600, letterSpacing: "0.2px", lineHeight: 1.1 }}>{t("nav.home")}</Box>
      </Box>

      {/* Redeem */}
      <Box component="button" onClick={() => navigate(routes.redeem)} sx={tabStyle(isRedeem)}>
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none">
          <path d="M7 8h13l-2.5 3L20 14H7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M17 16H4l2.5-3L4 10h13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <Box component="span" sx={{ fontSize: "10px", fontWeight: 600, letterSpacing: "0.2px", lineHeight: 1.1 }}>{t("nav.redeem")}</Box>
      </Box>

      {/* Center QR button */}
      <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "flex-end", position: "relative", height: "64px" }}>
        <Box
          component="button"
          onClick={onQrClick}
          aria-label="Show member QR code"
          sx={{
            position: "absolute",
            bottom: "18px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "58px",
            height: "58px",
            borderRadius: "50%",
            border: "3px solid #E8A84A",
            background: "linear-gradient(145deg, #F5A06A 0%, #E85A22 100%)",
            boxShadow: "0 4px 14px rgba(232,90,34,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            padding: 0,
            zIndex: 2,
            "&:active": { transform: "translateX(-50%) scale(0.96)" },
          }}
        >
          <svg viewBox="0 0 28 28" width="28" height="28">
            <circle cx="8" cy="8" r="3.2" fill="#1B3A5C"/>
            <circle cx="20" cy="8" r="3.2" fill="#2A9B9B"/>
            <circle cx="8" cy="20" r="3.2" fill="#2A9B9B"/>
            <circle cx="20" cy="20" r="3.2" fill="#7EC8E3"/>
            <path d="M8 8h12M8 8v12M20 8v12M8 20h12" stroke="rgba(255,255,255,0.55)" strokeWidth="1.4"/>
          </svg>
        </Box>
      </Box>

      {/* Wallet */}
      <Box component="button" onClick={() => navigate(routes.wallet)} sx={tabStyle(isWallet)}>
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none">
          <path d="M7 4.5h10a1 1 0 0 1 1 1V20l-3-1.8L12 20l-3-1.8L6 20V5.5a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/>
        </svg>
        <Box component="span" sx={{ fontSize: "10px", fontWeight: 600, letterSpacing: "0.2px", lineHeight: 1.1 }}>{t("nav.wallet")}</Box>
      </Box>

      {/* Account */}
      <Box component="button" onClick={() => navigate(routes.account)} sx={tabStyle(isAccount)}>
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none">
          <circle cx="12" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.7"/>
          <path d="M5 19.5c1.3-3.4 3.7-5.1 7-5.1s5.7 1.7 7 5.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
        </svg>
        <Box component="span" sx={{ fontSize: "10px", fontWeight: 600, letterSpacing: "0.2px", lineHeight: 1.1 }}>{t("nav.account")}</Box>
      </Box>
    </Box>
  );
}
